import { FC, PropsWithChildren, useMemo, useState } from 'react';
import { FilePlus } from 'lucide-react';
import { DropZone } from '@new/common/ui/Dropzone/Dropzone';
import { Label } from '@radix-ui/react-label';
import { SuccessPreview } from '../SuccessPreviewFile/SuccessPreviewFile';
import { ErrorPreview } from '../ErrorPreviewFile/ErrorPreviewFile';
import {
  useUploadOnboardingFileMutation,
  useDeleteOnboardingFileMutation,
} from '@teachur/http-client';
import { useCurrentUser } from '@hooks/users';
import { toast } from 'sonner';

type OnboardingFileUploadProps = PropsWithChildren<{
  label: string;
  index: 1 | 2;
}>;

export const OnboardingFileUpload: FC<OnboardingFileUploadProps> = ({ label, index }) => {
  const [localFile, setLocalFile] = useState<File | null>(null);
  const { data: currentUser } = useCurrentUser();
  const studentId = currentUser?.id;

  const url = currentUser?.onboarding?.[`idImage${index}`]?.url;

  const {
    mutate: uploadFile,
    isPending: isUploading,
    isError,
    reset,
    error,
  } = useUploadOnboardingFileMutation();

  const { mutateAsync: deleteFile, isPending: isDeleting } = useDeleteOnboardingFileMutation();

  const onUpload = (file: File) => {
    if (!studentId) return;
    setLocalFile(file);
    uploadFile({ file, index, studentId });
  };

  const onRetry = () => {
    if (!localFile || isUploading || !studentId) return;
    uploadFile({ file: localFile, index, studentId });
  };

  const onError = (error: string) => {
    toast.error(error);
  };

  const onRemoveFile = () => {
    if (isUploading || isDeleting || !studentId) return;

    setLocalFile(null);
    if (url) {
      deleteFile({ index, studentId });
    } else {
      reset?.();
    }
  };

  const fileNameFromUrl = useMemo(() => {
    if (!url) return '';
    try {
      const last = decodeURIComponent(url.split('/').pop() ?? '');
      const withoutNull = last.split('null')[0];
      return withoutNull.split('.')[0];
    } catch {
      return '';
    }
  }, [url]);

  const fileName = fileNameFromUrl || localFile?.name || '';
  const showSuccess = fileName && !isError && !isUploading;
  const showDropzone = !isError && !showSuccess;

  const errorMessage = error instanceof Error ? error.message : 'Upload Failed';
  return (
    <div className="flex flex-col gap-4">
      <Label className="text-left [&&]:[font-family:var(--font-montserrat)]! medium [&&]:[font-size:var(--fs-l5)]! [&&]:lg:[font-size:var(--fs-l3)]! [&&]:leading(--lh-b)! [&&]:text-(--neutral-black-NB1000)!">
        {label}
      </Label>
      {showDropzone && (
        <>
          <DropZone
            onUpload={onUpload}
            onError={onError}
            disabled={isUploading}
            loading={isUploading}
            accept=".jpg,.jpeg,.png"
          >
            <FilePlus
              className="text-(--neutral-black-NB600)
               w-6 h-6 md:w-10 md:h-10
               stroke-[0.5] md:stroke-1"
            />

            <p
              className="hidden md:block
               font-montserrat
               text-(--neutral-white-NW600)
               text-[14px]
               mb-[13px]"
            >
              Drop files here or
              <span className="text-(--primary-O900) ml-1 font-semibold underline">
                click to upload
              </span>
              <span className="block text-[13px]">JPEG, PNG less than 10MB</span>
            </p>
          </DropZone>
          <div
            className="hidden md:block text-left
               text-(--neutral-white-NW600)
               text-[14px]"
          >
            Your documents are securely encrypted and only accessible to authorized enrollment
            staff.
            <br />
            Please ensure they are clear and readable.
          </div>
        </>
      )}

      {showSuccess && (
        <SuccessPreview name={fileName} url={url} onRemove={onRemoveFile} isLoading={isDeleting} />
      )}

      {isError && (
        <ErrorPreview
          name={fileName}
          onRetry={onRetry}
          onRemove={onRemoveFile}
          message={errorMessage}
        />
      )}
    </div>
  );
};
